export type PostalCodeLookupCandidate = {
  prefecture: string
  city: string
  address: string
}

type PostalCodeLookupResponse = {
  postalCode: string
  candidates: PostalCodeLookupCandidate[]
}

type ZipCloudResult = {
  zipcode: string
  address1: string
  address2: string
  address3: string
}

type ZipCloudResponse = {
  message: string | null
  results: ZipCloudResult[] | null
  status: number
}

export class PostalCodeService {
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000
  private static readonly responseCache = new Map<
    string,
    { expiresAt: number; data: PostalCodeLookupResponse }
  >()
  private static readonly inFlightRequests = new Map<string, Promise<PostalCodeLookupResponse>>()

  async lookupPostalCode(postalCode: string): Promise<PostalCodeLookupResponse> {
    const normalizedPostalCode = postalCode.replace(/-/g, '').trim()
    if (!/^\d{7}$/.test(normalizedPostalCode)) {
      throw new Error('invalid-argument')
    }

    const now = Date.now()
    const cached = PostalCodeService.responseCache.get(normalizedPostalCode)
    if (cached) {
      if (cached.expiresAt > now) {
        return cached.data
      }
      PostalCodeService.responseCache.delete(normalizedPostalCode)
    }

    const inFlight = PostalCodeService.inFlightRequests.get(normalizedPostalCode)
    if (inFlight) {
      return inFlight
    }

    const requestPromise = (async () => {
      try {
        const response = await fetch(
          `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${encodeURIComponent(normalizedPostalCode)}`,
        )
        if (!response.ok) {
          throw new Error('internal')
        }

        const data = (await response.json()) as ZipCloudResponse
        const candidates = (data.results ?? []).map((r) => ({
          prefecture: r.address1,
          city: r.address2,
          address: r.address3,
        }))
        if (candidates.length === 0) {
          throw new Error('not-found')
        }

        const result: PostalCodeLookupResponse = {
          postalCode: normalizedPostalCode,
          candidates,
        }
        PostalCodeService.responseCache.set(normalizedPostalCode, {
          expiresAt: Date.now() + PostalCodeService.CACHE_TTL_MS,
          data: result,
        })
        return result
      } finally {
        PostalCodeService.inFlightRequests.delete(normalizedPostalCode)
      }
    })()

    PostalCodeService.inFlightRequests.set(normalizedPostalCode, requestPromise)
    return requestPromise
  }
}

